export default function PlymouthMNTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Plymouth Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how Plymouth travelers actually move helps us anticipate the passport urgencies that arise here. From corporate commuters on short-notice trips to medical conference schedules and Wayzata school district family travel, western metro travel has a unique rhythm.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">MSP — Minneapolis-Saint Paul International Airport</h3>
                <p className="text-gray-400 text-xs">About 18 miles from Plymouth — 20 minutes by car</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Plymouth's access to MSP via I-494 makes international travel very convenient. With nonstop service to Amsterdam, London, Paris, Tokyo, and Reykjavik, Plymouth residents connect directly to major international destinations. The airport's proximity means professionals can receive a last-minute assignment on Monday morning and be on an international flight by Thursday.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Check your passport expiration before every travel planning conversation, not after tickets are purchased. Most countries require six months validity beyond your return date.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-briefcase-4-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Corporate &amp; Medical Travel</h3>
                <p className="text-gray-400 text-xs">Medtronic, 3M, UnitedHealth Group, and major healthcare systems</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Plymouth residents work at companies with global operations — Medtronic devices require international trials and conferences, 3M has operations worldwide, and UnitedHealth Group employees travel extensively. Medical professionals from nearby healthcare systems attend international conferences with fixed schedules. Corporate travel assignments arrive with 2–3 weeks notice and cannot wait for standard processing.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Corporate travelers should check passport expiration at the start of each fiscal year. Set calendar reminders for the entire team.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-ship-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Family &amp; School Vacation Travel</h3>
                <p className="text-gray-400 text-xs">Spring break, summer European tours, and holiday cruises</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Plymouth families with children in the Wayzata and Robbinsdale school districts plan spring break trips, summer European tours, and holiday cruises. Children's passports are only valid for 5 years and expire faster than adult passports. Many families discover expiration issues only after booking, leaving just weeks to resolve a critical document problem before departure.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Check every family member's passport at the start of the school year. Children's passports expire in 5 years, not 10. Plan renewals 6 months before school trips.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-heart-pulse-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Medical Conference &amp; Mission Travel</h3>
                <p className="text-gray-400 text-xs">Healthcare professionals with international schedules</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Medical professionals from nearby healthcare systems travel for conferences, continuing education, and medical mission work. Conference schedules and mission trip dates are fixed and non-negotiable — you cannot miss a conference you are presenting at because of a passport delay. Standard processing cannot accommodate these compressed timelines.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Medical conference travelers should apply for passport renewal at least 6 months before known conference dates.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-building-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Corporate Event &amp; Conference Travel</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Plymouth professionals host and attend major industry conferences and events that draw international attendees. Event organizers, keynote speakers, and corporate attendees flying in from abroad — or Plymouth professionals traveling to partner events overseas — sometimes discover passport issues during event planning. We accommodate urgent corporate timelines with same-day document review and prioritized submission.
            </p>
          </div>
          <a href="https://fastpassportcenter.com/checkout/" className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0">
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}